import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { useMemo } from 'react'
import {
  type UserLoyalty,
  type Achievement,
  type ActiveBonus,
  type AchievementLevel,
  ACHIEVEMENT_LEVELS,
  getAchievementByPoints,
  calculatePointsToNextLevel,
  calculateProgressPercentage,
  getAchievementAnnouncementText
} from '../types/loyalty'
import { loyaltyService } from '../services/loyaltyService'
import { updatePublicProfileLoyaltyFrame, getUserLoyaltyFramePreference } from '../services/nicknameService'

interface LoyaltyState {
  // User loyalty data
  userLoyalty: UserLoyalty | null
  isLoading: boolean
  error: string | null

  // Active bonuses
  activeBonuses: ActiveBonus[]

  // UI state
  showAchievementModal: boolean
  pendingAchievement: Achievement | null
  recentPointsGain: {
    amount: number
    source: string
    timestamp: Date
  } | null
  showMyLoyaltyFrame: boolean

  // Actions
  initializeLoyalty: (userId: string) => Promise<void>
  refreshLoyalty: (userId: string) => Promise<void>
  checkAndUnlockAchievements: () => Promise<Achievement[]>
  updateUserLoyalty: (loyalty: Partial<UserLoyalty>) => void
  showAchievementCelebration: (achievement: Achievement) => void
  hideAchievementModal: () => void
  clearRecentPointsGain: () => void
  loadActiveBonuses: () => Promise<void>
  setShowMyLoyaltyFrame: (show: boolean) => Promise<void>
  reset: () => void // Reset store state on logout

  // Getters
  getCurrentLevel: () => AchievementLevel
  getPointsToNextLevel: () => number
  getProgressPercentage: () => number
  getUnlockedAchievements: () => Achievement[]
  hasUnlockedAchievement: (level: AchievementLevel) => boolean
}

export const useLoyaltyStore = create<LoyaltyState>()(
  persist(
    (set, get) => ({
      // Initial state
      userLoyalty: null,
      isLoading: false,
      error: null,
      activeBonuses: [],
      showAchievementModal: false,
      pendingAchievement: null,
      recentPointsGain: null,
      showMyLoyaltyFrame: true,

      // Initialize loyalty system for a user
      initializeLoyalty: async (userId: string) => {
        set({ isLoading: true, error: null })

        try {
          const userLoyalty = await loyaltyService.getUserLoyalty(userId)

          if (!userLoyalty) {
            // User loyalty not found - this is normal for new users
            // Will be initialized automatically on first purchase
            set({
              userLoyalty: null,
              isLoading: false,
              error: null // No error - this is expected for new users
            })
            return
          }

          // Calculate derived properties for the store
          const pointsToNext = calculatePointsToNextLevel(userLoyalty.totalPoints)
          const progress = calculateProgressPercentage(userLoyalty.totalPoints)

          const enhancedLoyalty: UserLoyalty = {
            ...userLoyalty,
            pointsToNextLevel: pointsToNext,
            progressPercentage: progress,
            lastUpdated: new Date(),
            totalSpent: 0 // Will be calculated from purchase history in the future
          }

          set({ userLoyalty: enhancedLoyalty, isLoading: false })

          // Load user's loyalty frame preference
          try {
            const showFrame = await getUserLoyaltyFramePreference(userId)
            set({ showMyLoyaltyFrame: showFrame })
          } catch (error) {
            console.warn('Failed to load loyalty frame preference:', error)
            // Keep default value (true)
          }

        } catch (error) {
          console.error('Error initializing loyalty:', error)
          set({
            error: 'Error al inicializar el sistema de lealtad',
            isLoading: false
          })
        }
      },

      // Refresh loyalty data from backend
      refreshLoyalty: async (userId: string) => {
        try {
          const userLoyalty = await loyaltyService.getUserLoyalty(userId)

          if (userLoyalty) {
            // Calculate derived properties for the store
            const pointsToNext = calculatePointsToNextLevel(userLoyalty.totalPoints)
            const progress = calculateProgressPercentage(userLoyalty.totalPoints)

            const enhancedLoyalty: UserLoyalty = {
              ...userLoyalty,
              pointsToNextLevel: pointsToNext,
              progressPercentage: progress,
              lastUpdated: new Date(),
              totalSpent: userLoyalty.totalSpent || 0
            }

            set({ userLoyalty: enhancedLoyalty })
          }
        } catch (error) {
          console.error('Error refreshing loyalty:', error)
        }
      },

      // Note: Points are now awarded by backend during purchase process

      // Check and unlock new achievements
      checkAndUnlockAchievements: async () => {
        const { userLoyalty } = get()
        if (!userLoyalty) return []

        const newAchievements: Achievement[] = []
        const currentUnlockedLevels = new Set(
          userLoyalty.achievements.map(a => a.level)
        )

        // Check each achievement level
        for (const [level, achievementData] of Object.entries(ACHIEVEMENT_LEVELS)) {
          const typedLevel = level as AchievementLevel

          if (
            userLoyalty.totalPoints >= achievementData.pointsRequired &&
            !currentUnlockedLevels.has(typedLevel)
          ) {
            const newAchievement: Achievement = {
              id: `achievement_${typedLevel}`,
              ...achievementData,
              unlockedAt: new Date(),
              isUnlocked: true
            }

            newAchievements.push(newAchievement)
          }
        }

        if (newAchievements.length > 0) {
          const updatedAchievements = [
            ...userLoyalty.achievements,
            ...newAchievements
          ]

          const updatedLoyalty: UserLoyalty = {
            ...userLoyalty,
            achievements: updatedAchievements,
            currentLevel: getAchievementByPoints(userLoyalty.totalPoints)
          }

          set({ userLoyalty: updatedLoyalty })

          // Show celebration for the highest achievement unlocked
          const highestAchievement = newAchievements.reduce((highest, current) =>
            current.pointsRequired > highest.pointsRequired ? current : highest
          )

          get().showAchievementCelebration(highestAchievement)

          // Announce achievement for screen readers
          const announcement = getAchievementAnnouncementText(highestAchievement.level)
          console.log('Achievement announcement:', announcement)
        }

        return newAchievements
      },

      // Update user loyalty data
      updateUserLoyalty: (loyalty: Partial<UserLoyalty>) => {
        const { userLoyalty } = get()
        if (!userLoyalty) return

        const updated = { ...userLoyalty, ...loyalty, lastUpdated: new Date() }
        set({ userLoyalty: updated })
      },

      // Show achievement celebration modal
      showAchievementCelebration: (achievement: Achievement) => {
        set({
          showAchievementModal: true,
          pendingAchievement: achievement
        })

        // Auto-hide after 5 seconds for accessibility
        setTimeout(() => {
          const currentState = get()
          if (currentState.showAchievementModal && currentState.pendingAchievement?.id === achievement.id) {
            get().hideAchievementModal()
          }
        }, 5000)
      },

      // Hide achievement modal
      hideAchievementModal: () => {
        set({
          showAchievementModal: false,
          pendingAchievement: null
        })
      },

      // Clear recent points gain notification
      clearRecentPointsGain: () => {
        set({ recentPointsGain: null })
      },

      // Load active bonuses
      loadActiveBonuses: async () => {
        try {
          const activeBonuses = await loyaltyService.getActiveBonuses()
          set({ activeBonuses })
        } catch (error) {
          console.error('Error loading active bonuses:', error)
          set({ error: 'Error al cargar bonos activos' })
        }
      },


      // Set my loyalty frame visibility preference
      setShowMyLoyaltyFrame: async (show: boolean) => {
        const { userLoyalty } = get()
        if (!userLoyalty) return

        try {
          // Update in public profile (this is what other users see)
          await updatePublicProfileLoyaltyFrame(userLoyalty.userId, show)

          // Update local state
          set({ showMyLoyaltyFrame: show })
        } catch (error) {
          console.error('Error updating loyalty frame preference:', error)
        }
      },

      // Reset store state on logout - prevents cross-user data leakage
      reset: () => {
        set({
          userLoyalty: null,
          isLoading: false,
          error: null,
          activeBonuses: [],
          showAchievementModal: false,
          pendingAchievement: null,
          recentPointsGain: null,
          showMyLoyaltyFrame: true
        })
      },

      // Getters
      getCurrentLevel: () => {
        const { userLoyalty } = get()
        return userLoyalty?.currentLevel || 'explorer'
      },

      getPointsToNextLevel: () => {
        const { userLoyalty } = get()
        return userLoyalty?.pointsToNextLevel || 500
      },

      getProgressPercentage: () => {
        const { userLoyalty } = get()
        return userLoyalty?.progressPercentage || 0
      },

      getUnlockedAchievements: () => {
        const { userLoyalty } = get()
        return userLoyalty?.achievements.filter(a => a.isUnlocked) || []
      },

      hasUnlockedAchievement: (level: AchievementLevel) => {
        const { userLoyalty } = get()
        return userLoyalty?.achievements.some(a => a.level === level && a.isUnlocked) || false
      }
    }),
    {
      name: 'loyalty-store',
      // Only persist essential data
      partialize: (state) => ({
        userLoyalty: state.userLoyalty,
        activeBonuses: state.activeBonuses,
        showMyLoyaltyFrame: state.showMyLoyaltyFrame
      })
    }
  )
)

// Selector hooks for performance optimization
export const useLoyaltyPoints = () => useLoyaltyStore(state => state.userLoyalty?.totalPoints || 0)
export const useLoyaltyLevel = () => useLoyaltyStore(state => state.userLoyalty?.currentLevel || 'explorer')
export const useLoyaltyProgress = () => {
  const pointsToNext = useLoyaltyStore(state => state.userLoyalty?.pointsToNextLevel || 500)
  const percentage = useLoyaltyStore(state => state.userLoyalty?.progressPercentage || 0)

  return useMemo(() => ({ pointsToNext, percentage }), [pointsToNext, percentage])
}
export const useAchievements = () => {
  const achievements = useLoyaltyStore(state => state.userLoyalty?.achievements)
  return useMemo(() => achievements || [], [achievements])
}
export const useRecentPointsGain = () => useLoyaltyStore(state => state.recentPointsGain)
export const useAchievementModal = () => {
  const isOpen = useLoyaltyStore(state => state.showAchievementModal)
  const achievement = useLoyaltyStore(state => state.pendingAchievement)
  const onClose = useLoyaltyStore(state => state.hideAchievementModal)

  return useMemo(() => ({ isOpen, achievement, onClose }), [isOpen, achievement, onClose])
}

export const useShowMyLoyaltyFrame = () => useLoyaltyStore(state => state.showMyLoyaltyFrame)
export const useSetShowMyLoyaltyFrame = () => useLoyaltyStore(state => state.setShowMyLoyaltyFrame)

export default useLoyaltyStore